import React, { useState } from 'react';
import { Activity, RefreshCw } from 'lucide-react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';

/**
 * Config → « Consommation IA ».
 *
 * L'ENTRÉE DE MENU N'EXISTE QUE SI LE SERVEUR RÉPOND : ConfigPage sonde
 * GET /config/ia/consommation au chargement, et n'affiche l'onglet que sur
 * une réponse 200. Un 403 (ou 404/501) = pas d'entrée du tout. C'est le
 * serveur qui décide QUI voit cet écran — le front ne code aucune liste
 * d'ayants droit : tout ce qui est écrit ici est public (le bundle JS se lit),
 * donc un e-mail ou un rôle en dur ne cacherait rien à personne. La seule
 * protection réelle est le 403 du serveur ; ici on se contente de ne pas
 * dessiner de porte quand le serveur dit qu'elle est fermée.
 *
 * Contrat : { lignes[], totaux_par_feature, periode, note } — champs des
 * lignes tolérés dynamiquement (le back peut enrichir sans casser le front).
 */
export const ROUTE_CONSO_IA = `${BASE}/config/ia/consommation`;

// Libellés français des colonnes connues ; toute clé inconnue est affichée
// telle quelle (prettifiée) plutôt que perdue.
const LIBELLES = {
  feature: 'Fonctionnalité',
  mois: 'Mois',
  modele: 'Modèle', model: 'Modèle',
  tokens_entree: 'Jetons entrants', input_tokens: 'Jetons entrants',
  tokens_sortie: 'Jetons sortants', output_tokens: 'Jetons sortants',
  tokens: 'Jetons',
  appels: 'Appels', requetes: 'Requêtes', count: 'Appels',
  cout: 'Coût estimé', cout_estime: 'Coût estimé', cost: 'Coût estimé',
  cout_usd: 'Coût estimé (USD)', cost_usd: 'Coût estimé (USD)',
};
const ORDRE = Object.keys(LIBELLES);
const estCle = (k, ...prefixes) => prefixes.some(p => k === p || k.startsWith(`${p}_`));
const joli = (k) => LIBELLES[k] || String(k).replace(/_/g, ' ');

// Un coût s'affiche avec ses décimales (0.0042 compte), un volume sans.
const fmtVal = (cle, v) => {
  if (v == null) return '—';
  if (typeof v === 'number') {
    const decimales = estCle(cle, 'cout', 'cost') ? { minimumFractionDigits: 2, maximumFractionDigits: 4 } : {};
    return v.toLocaleString('fr-CH', decimales);
  }
  return String(v);
};

// Colonnes = union des clés présentes, dans l'ordre des libellés connus puis
// l'ordre d'apparition — sans jamais supposer que toutes les lignes sont pleines.
const colonnesDe = (lignes, exclues = []) => {
  const vues = [];
  for (const l of lignes) for (const k of Object.keys(l || {})) {
    if (!exclues.includes(k) && !vues.includes(k)) vues.push(k);
  }
  return vues.sort((a, b) => {
    const ia = ORDRE.indexOf(a), ib = ORDRE.indexOf(b);
    return (ia === -1 ? ORDRE.length : ia) - (ib === -1 ? ORDRE.length : ib);
  });
};

const Tableau = ({ colonnes, lignes }) => (
  <div className="overflow-x-auto border border-rule rounded-xl">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-paper text-left">
          {colonnes.map(c => (
            <th key={c} className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-faint ${typeof lignes[0]?.[c] === 'number' ? 'text-right' : ''}`}>{joli(c)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lignes.map((l, i) => (
          <tr key={i} className="border-t border-rule">
            {colonnes.map(c => (
              <td key={c} className={`px-3 py-2 text-ink ${typeof l?.[c] === 'number' ? 'text-right font-mono text-[13px]' : ''}`}>{fmtVal(c, l?.[c])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const fmtPeriode = (p) => {
  if (!p) return null;
  if (typeof p === 'string') return p;
  const debut = p.debut || p.from || p.start, fin = p.fin || p.to || p.end;
  if (debut || fin) return [debut, fin].filter(Boolean).join(' → ');
  return null;
};

export default function ConsommationIA({ initial, showNotification }) {
  const [data, setData] = useState(initial || null);
  const [chargement, setChargement] = useState(false);

  const recharger = async () => {
    setChargement(true);
    try {
      const r = await fetch(ROUTE_CONSO_IA, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      setData(j.data || j);
    } catch (e) {
      showNotification?.('Actualisation impossible : ' + e.message, 'error');
    } finally { setChargement(false); }
  };

  if (!data) return null;
  const lignes = Array.isArray(data.lignes) ? data.lignes : [];
  const periode = fmtPeriode(data.periode);

  // Totaux par fonctionnalité : objet { feature: montant } ou { feature: {…} },
  // ou déjà un tableau de lignes — dans tous les cas, un tableau à afficher.
  const totauxBruts = data.totaux_par_feature;
  const totaux = Array.isArray(totauxBruts)
    ? totauxBruts
    : totauxBruts && typeof totauxBruts === 'object'
      ? Object.entries(totauxBruts).map(([feature, v]) =>
          (v && typeof v === 'object') ? { feature, ...v } : { feature, cout_estime: v })
      : [];

  // Détail groupé par mois, les plus récents d'abord (les clés mois « 2026-08 »
  // se comparent en texte) ; sans champ mois, un seul bloc.
  const parMois = new Map();
  for (const l of lignes) {
    const m = l?.mois ?? '';
    if (!parMois.has(m)) parMois.set(m, []);
    parMois.get(m).push(l);
  }
  const moisTries = [...parMois.keys()].sort((a, b) => String(b).localeCompare(String(a)));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-ink-strong flex items-center gap-2">
          <Activity className="w-4 h-4 text-accent" /> Consommation IA
          {periode && <span className="text-xs font-medium text-faint">· {periode}</span>}
        </h2>
        <button onClick={recharger} disabled={chargement}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-rule rounded-lg text-xs font-semibold text-muted hover:bg-paper disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${chargement ? 'animate-spin' : ''}`} /> Actualiser
        </button>
      </div>

      {/* Le bandeau dit ce que sont ces chiffres — une estimation, pas une facture. */}
      <div className="border rounded-xl px-4 py-3 text-[13px]" style={{ borderColor: 'rgba(180,83,9,0.30)', background: 'rgba(180,83,9,0.06)', color: 'var(--warn)' }}>
        Estimation d'après les tarifs publics des modèles — ce n'est pas une facture.
        {data.note && <div className="mt-1" style={{ opacity: 0.85 }}>{data.note}</div>}
      </div>

      {totaux.length > 0 && (
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wide text-faint mb-2">Totaux par fonctionnalité</div>
          <Tableau colonnes={colonnesDe(totaux)} lignes={totaux} />
        </div>
      )}

      {lignes.length === 0 ? (
        <p className="text-sm text-faint">Aucune consommation enregistrée sur la période.</p>
      ) : (
        <div className="space-y-4">
          <div className="text-[11px] font-bold uppercase tracking-wide text-faint">Détail par mois et par modèle</div>
          {moisTries.map(m => (
            <div key={m || 'sans-mois'}>
              {m && <div className="text-[13px] font-semibold text-ink mb-1.5 font-mono">{m}</div>}
              <Tableau colonnes={colonnesDe(parMois.get(m), ['mois'])} lignes={parMois.get(m)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
